import { useEffect, useState, useSyncExternalStore } from "react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";

/** Marketing site. */
export const FRANKLIN_URL = "https://frnkln.ai";

/** Sign-up destination for the auth pages. */
export const FRANKLIN_SIGNUP_URL = "https://dev.franklin.ee/signup";

/** Legal pages users consent to (see TermsOfServiceModal). */
export const FRANKLIN_TERMS_URL = `${FRANKLIN_URL}/terms`;
export const FRANKLIN_PRIVACY_URL = `${FRANKLIN_URL}/privacy`;

/**
 * The wordmark ships as a red SVG, so it is drawn as a CSS mask over
 * `bg-current` (same technique as `FranklinIcon`) to render it white on the
 * dark panel.
 */
const WORDMARK_MASK = "url(/franklin-logo-full.svg)";

// Typewriter cadence, in ms — mirrors the brand mock and the landing hero.
const TYPE_MS = 70;
const DELETE_MS = 38;
const HOLD_MS = 2600;
const GAP_MS = 650;
const START_MS = 500;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function subscribeReducedMotion(onChange: () => void): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return () => undefined;
  }
  const query = window.matchMedia("(prefers-reduced-motion: reduce)");
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

function randomBelow(bound: number): number {
  const slot = new Uint32Array(1);
  globalThis.crypto.getRandomValues(slot);
  return slot[0] % bound;
}

function shuffle(items: string[]): string[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = randomBelow(i + 1);
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/**
 * Drives the type / hold / delete / next-line cycle, pushing each frame of text
 * to `render`. The first entry always opens; the rest cycle in a reshuffled
 * order. Returns a cleanup that stops the cycle.
 */
function runTypewriter(lines: string[], render: (text: string) => void): () => void {
  let timer: ReturnType<typeof setTimeout>;
  let queue: string[] = [];
  let current = lines[0];
  let length = 0;
  let deleting = false;

  const nextLine = () => {
    if (queue.length === 0) {
      queue = shuffle(lines.filter((line) => line !== current));
    }
    return queue.shift() ?? current;
  };

  const tick = () => {
    if (deleting) {
      length -= 1;
      render(current.slice(0, length));
      if (length > 0) {
        timer = setTimeout(tick, DELETE_MS);
        return;
      }
      deleting = false;
      current = nextLine();
      timer = setTimeout(tick, GAP_MS);
      return;
    }

    length += 1;
    render(current.slice(0, length));
    if (length < current.length) {
      timer = setTimeout(tick, TYPE_MS);
      return;
    }
    deleting = true;
    timer = setTimeout(tick, HOLD_MS);
  };

  timer = setTimeout(tick, START_MS);
  return () => clearTimeout(timer);
}

/**
 * Rotates the brand one-liners; reduced motion shows the first one statically.
 * Tracks the OS setting live, so toggling it mid-session stops or restarts the
 * animation.
 */
function useTypewriter(lines: string[]): string {
  const reducedMotion = useSyncExternalStore(subscribeReducedMotion, prefersReducedMotion);
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (reducedMotion || lines.length === 0) return;
    return runTypewriter(lines, setTyped);
  }, [lines, reducedMotion]);

  // The reduced-motion line is derived during render, so the effect never has
  // to set state synchronously just to show something.
  return reducedMotion ? (lines[0] ?? "") : typed;
}

interface LoginBrandPanelProps {
  className?: string;
}

/** Signature red glow, lifted from the landing hero. */
const GLOW_STYLE = {
  background:
    "radial-gradient(760px 620px at 50% 40%, rgba(239,65,41,.16), transparent 62%)," +
    "radial-gradient(1100px 900px at 78% 108%, rgba(239,65,41,.08), transparent 60%)",
};

const WORDMARK_STYLE = {
  WebkitMaskImage: WORDMARK_MASK,
  maskImage: WORDMARK_MASK,
  WebkitMaskRepeat: "no-repeat",
  maskRepeat: "no-repeat",
  WebkitMaskPosition: "left center",
  maskPosition: "left center",
  WebkitMaskSize: "contain",
  maskSize: "contain",
};

const LINK_FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-neutral-900";

function BrandWordmark() {
  return (
    <a
      href={FRANKLIN_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={cn("relative z-10 inline-flex rounded-sm focus-visible:ring-offset-4", LINK_FOCUS)}
      aria-label="Franklin"
    >
      <span aria-hidden className="block h-[22px] w-[103px] bg-current" style={WORDMARK_STYLE} />
    </a>
  );
}

function BrandFooter() {
  return (
    <div className="relative z-10 hidden items-center justify-between gap-4 text-[13px] text-white/50 lg:flex">
      <span>© {new Date().getFullYear()} Franklin</span>
    </div>
  );
}

/**
 * LoginBrandPanel — the dark half of the login screen: the Franklin wordmark,
 * the rotating brand one-liner, and the copyright footer. Always dark
 * (it mirrors the landing hero), so it uses brand colours directly rather than
 * theme tokens.
 */
export function LoginBrandPanel({ className }: LoginBrandPanelProps) {
  const { t } = useLanguage();
  const line = useTypewriter(t.forms.login.taglines);

  return (
    <section
      className={cn(
        "relative flex flex-col justify-between overflow-hidden bg-neutral-900 px-7 py-7 text-white lg:basis-[52%] lg:px-14 lg:py-11",
        className
      )}
    >
      <div aria-hidden className="pointer-events-none absolute inset-0" style={GLOW_STYLE} />

      <BrandWordmark />

      <div className="relative z-10 my-6 max-w-[480px] lg:my-auto">
        <h2 className="flex min-h-[4.5em] flex-col justify-center text-[26px] font-bold leading-[1.1] tracking-[-0.02em] lg:text-[clamp(1.875rem,3.4vw,2.75rem)]">
          {/* Screen readers get the opening tagline once; the animated copy is
              decorative and would otherwise re-announce on every typed frame. */}
          <span className="sr-only">{t.forms.login.taglines[0]}</span>
          <span aria-hidden>
            <span data-testid="typewriter">{line}</span>
            <span className="ml-1.5 inline-block h-[0.92em] w-[0.5ch] translate-y-[0.1em] bg-primary motion-safe:animate-cursor-blink" />
          </span>
        </h2>
      </div>

      <BrandFooter />
    </section>
  );
}

LoginBrandPanel.displayName = "LoginBrandPanel";
